/**
 * Feature 137: the free 15-minute service coordination call with the Care Concierge team.
 *
 * One button + Calendly modal for every surface that offers the call. A booking by a signed-in
 * advisor is recorded as a DISCOVERY_CALL engagement; anyone else (care seekers, guests on the FP
 * shareable page) books the same call without a record, since a fact with no advisor behind it
 * cannot be reported on.
 *
 * Lives in shared because the dashboard and the provider directory both render it, and features do
 * not import from one another.
 */

import { useState } from 'react';
import { Calendar } from 'lucide-react';
import { apiClient } from '@/core/api/client';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { Button, type ButtonProps } from '@/shared/components/ui/button';
import { CalendlyModal } from '@/shared/components/CalendlyModal';
import { cn } from '@/shared/utils/cn';

// `||` (not `??`) + trim so a missing OR blank env value both fall back.
const SERVICE_COORDINATION_CALL_URL =
  import.meta.env.VITE_CARE_CONCIERGE_CALENDLY_URL?.trim() ||
  'https://calendly.com/d/cxg5-sjq-r5p/bquest-service-coordination-call';

type ServiceCoordinationCallButtonProps = Pick<ButtonProps, 'variant' | 'size' | 'className'>;

// Fire-and-forget: the call is already booked in Calendly, so a failed record must not get in
// the advisor's way — it only costs one analytics row.
const recordBooking = (calendlyEventUri: string) => {
  apiClient
    .post('/financial-advisors/me/care-concierge/discovery-calls', { calendlyEventUri })
    .catch((error: unknown) => console.error('Failed to record service coordination call', error));
};

export const ServiceCoordinationCallButton = ({
  variant = 'outline',
  size,
  className,
}: ServiceCoordinationCallButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={cn('gap-2', className)}
        onClick={() => setIsOpen(true)}
      >
        <Calendar className="h-4 w-4" aria-hidden="true" />
        Schedule a service coordination call
      </Button>

      <CalendlyModal
        open={isOpen}
        onOpenChange={setIsOpen}
        url={SERVICE_COORDINATION_CALL_URL}
        title="Schedule a service coordination call"
        description="Select a time for a free 15-minute call with our Care Concierge team"
        onEventScheduled={user?.role === 'FINANCIAL_ADVISOR' ? recordBooking : undefined}
      />
    </>
  );
};
